import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Card,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Link as MuiLink,
  Divider,
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  Cancel,
  Business,
  LocationOn,
  Language,
  Description,
  Email,
  Phone,
  Visibility,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

interface PickupAddress {
  id: number;
  name: string;
  latitude?: string;
  longitude?: string;
  address?: string;
  street?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  location_type?: string;
  is_default?: number;
}

interface VendorApplication {
  id: number;
  first_name: string;
  last_name: string;
  user_email: string;
  user_contact: string;
  business_name: string;
  company_address: string;
  company_location: string;
  website: string | null;
  tax_registration_number: string | null;
  business_type: string | null;
  certificate_name: string | null;
  registration_certificate: string | null;
  approval_status: 'pending' | 'approved' | 'rejected' | 'blocked';
  approval_notes: string | null;
  createdAt: string;
  pickup_addresses?: PickupAddress[];
}

const BASE_URL = process.env.REACT_APP_SERVER_ENDPOINT || '';
const AUTH_KEY = '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3';

const authHeaders = () => {
  const token = localStorage.getItem('admin_token') || localStorage.getItem('vendor_token');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    authkey: AUTH_KEY,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const pickupAddressText = (pickup?: PickupAddress) => {
  if (!pickup) return '';
  if (pickup.address) return pickup.address;
  return [pickup.street, pickup.city, pickup.state, pickup.postal_code].filter(Boolean).join(', ');
};

const statusColor = (status: string) => {
  switch (status) {
    case 'approved': return { bgcolor: '#e3f5e9', color: '#0f7b3f' };
    case 'rejected': return { bgcolor: '#fdecea', color: '#c0362c' };
    case 'blocked': return { bgcolor: '#2f2f2f', color: '#ffffff' };
    default:         return { bgcolor: '#fff4e5', color: '#a05a00' };
  }
};

const VendorApplications = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tab, setTab] = useState<'pending' | 'approved' | 'rejected' | 'blocked' | 'all'>(() => (localStorage.getItem('tab_vendor_applications') as any) || 'pending');
  const [applications, setApplications] = useState<VendorApplication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [rejectTarget, setRejectTarget] = useState<VendorApplication | null>(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [actingId, setActingId] = useState<number | null>(null);
  const [viewTarget, setViewTarget] = useState<VendorApplication | null>(null);

  // When we arrive here from a notification click, we're handed the specific
  // vendor_id to show — open that vendor's own detail dialog instead of just
  // dumping the person on the full, unfiltered list.
  const location = useLocation();
  const navigate = useNavigate();
  const openVendorId = (location.state as any)?.openVendorId;

  useEffect(() => {
    if (openVendorId != null && tab !== 'all') {
      setTab('all');
    }
  }, [openVendorId, tab]);

  useEffect(() => {
    if (openVendorId == null || applications.length === 0) return;
    const match = applications.find((a) => Number(a.id) === Number(openVendorId));
    if (match) {
      setViewTarget(match);
      // Clear the nav state so leaving and returning to this page (or
      // switching tabs) doesn't keep reopening the same dialog.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [openVendorId, applications, navigate, location.pathname]);

  const handleDrawerToggle = () => setMobileOpen((p) => !p);

  const fetchApplications = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/admin/vendorApplications?status=${tab}`, {
        headers: authHeaders(),
      });
      const result = await res.json();
      if (!result.error) {
        setApplications(result.data || []);
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to load applications', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Network error while loading applications', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [tab]);

  useEffect(() => { localStorage.setItem('tab_vendor_applications', tab); fetchApplications(); }, [tab, fetchApplications]);

  const handleApprove = async (app: VendorApplication) => {
    setActingId(app.id);
    try {
      const res = await fetch(`${BASE_URL}/admin/vendorApplications/approve`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ vendor_id: app.id }),
      });
      const result = await res.json();
      if (!result.error) {
        setSnackbar({ open: true, message: `${app.business_name} approved`, severity: 'success' });
        fetchApplications();
      } else {
        setSnackbar({ open: true, message: result.message || 'Approval failed', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Network error while approving', severity: 'error' });
    } finally {
      setActingId(null);
    }
  };

  const handleReconsider = async (app: VendorApplication) => {
    setActingId(app.id);
    try {
      const res = await fetch(`${BASE_URL}/admin/vendorApplications/reconsider`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ vendor_id: app.id }),
      });
      const result = await res.json();
      if (!result.error) {
        setSnackbar({ open: true, message: `${app.business_name} moved back to Pending`, severity: 'success' });
        fetchApplications();
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to move to Pending', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Network error while moving to Pending', severity: 'error' });
    } finally {
      setActingId(null);
    }
  };

  const handleUnblock = async (app: VendorApplication) => {
    setActingId(app.id);
    try {
      const res = await fetch(`${BASE_URL}/admin/vendorApplications/unblock`, { method:'POST', headers:authHeaders(), body:JSON.stringify({vendor_id:app.id}) });
      const result = await res.json();
      if (!result.error) { setSnackbar({open:true,message:`${app.business_name} unblocked and restored to Approved`,severity:'success'}); fetchApplications(); }
      else setSnackbar({open:true,message:result.message || 'Unblock failed',severity:'error'});
    } catch { setSnackbar({open:true,message:'Network error while unblocking',severity:'error'}); }
    finally { setActingId(null); }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    if (!rejectNotes.trim()) { setSnackbar({open:true,message:'Rejection reason is mandatory.',severity:'error'}); return; }
    setActingId(rejectTarget.id);
    try {
      const res = await fetch(`${BASE_URL}/admin/vendorApplications/${rejectTarget.approval_status === 'approved' ? 'block' : 'reject'}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ vendor_id: rejectTarget.id, notes: rejectNotes || undefined }),
      });
      const result = await res.json();
      if (!result.error) {
        setSnackbar({ open: true, message: `${rejectTarget.business_name} ${rejectTarget.approval_status === 'approved' ? 'blocked' : 'rejected'}`, severity: 'success' });
        setRejectTarget(null);
        setRejectNotes('');
        fetchApplications();
      } else {
        setSnackbar({ open: true, message: result.message || 'Rejection failed', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Network error while rejecting', severity: 'error' });
    } finally {
      setActingId(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f1f2f4' }}>
      {/* Sidebar */}
      <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
      </Box>

      <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}
      >
        <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
      </Drawer>

      {/* Main */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <Box sx={{ flexShrink: 0 }}><AdminHeader onToggleMenu={handleDrawerToggle} /></Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
          <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 3, pb: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>Vendor Applications</Typography>
              <Typography variant="body2" sx={{ color: '#6d7175' }}>
                Review company details before a seller can upload products.
              </Typography>
            </Box>
            <Button variant="outlined" startIcon={<Refresh />} onClick={fetchApplications} disabled={isLoading}
              sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500 }}
            >Refresh</Button>
          </Box>

          <Box sx={{ px: 3 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
              <Tab value="pending" label="Pending" />
              <Tab value="approved" label="Approved" />
              <Tab value="rejected" label="Rejected" />
              <Tab value="blocked" label="Blocked" />
              <Tab value="all" label="All" />
            </Tabs>
          </Box>

          <Box sx={{ px: 3, pb: 3 }}>
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={32} sx={{ color: '#008060' }} />
              </Box>
            )}

            {!isLoading && applications.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary">No {tab !== 'all' ? tab : ''} applications</Typography>
              </Box>
            )}

            {!isLoading && applications.map((app) => (
              <Card key={app.id} sx={{ p: 3, mb: 2, borderRadius: '10px', border: '1px solid #e1e3e5' }} elevation={0}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Business sx={{ fontSize: 18, color: '#6d7175' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#202223' }}>
                        {app.business_name || 'Unnamed company'}
                      </Typography>
                      <Chip label={app.approval_status} size="small" sx={{ ...statusColor(app.approval_status), fontWeight: 600, textTransform: 'capitalize' }} />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#6d7175' }}>
                      Applicant: {app.first_name} {app.last_name}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => setViewTarget(app)}
                      sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223' }}
                    >
                      View Details
                    </Button>
                  {app.approval_status === 'pending' && (
                    <>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        startIcon={<CheckCircle />}
                        disabled={actingId === app.id}
                        onClick={() => handleApprove(app)}
                        sx={{ textTransform: 'none', bgcolor: '#008060', '&:hover': { bgcolor: '#006e52' } }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Cancel />}
                        disabled={actingId === app.id}
                        onClick={() => { setRejectTarget(app); setRejectNotes(''); }}
                        sx={{ textTransform: 'none' }}
                      >
                        Reject
                      </Button>
                    </Box>
                    </>
                  )}
                  {app.approval_status === 'approved' && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Cancel />}
                      disabled={actingId === app.id}
                      onClick={() => { setRejectTarget(app); setRejectNotes(''); }}
                      sx={{ textTransform: 'none' }}
                    >
                      Block / Reject
                    </Button>
                  )}
                  {app.approval_status === 'blocked' && (
                    <Button size="small" variant="contained" color="success" onClick={() => handleUnblock(app)} disabled={actingId === app.id}>Unblock</Button>
                  )}
                  {app.approval_status === 'rejected' && (
                    <Button
                      variant="outlined"
                      startIcon={<CheckCircle />}
                      disabled={actingId === app.id}
                      onClick={() => handleReconsider(app)}
                      sx={{ textTransform: 'none', borderColor: '#008060', color: '#008060' }}
                    >
                      Move to Pending
                    </Button>
                  )}
                  </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationOn sx={{ fontSize: 16, color: '#6d7175', mt: 0.2 }} />
                    <Box>
                      {app.pickup_addresses?.length ? (
                        <>
                          <Typography variant="caption" color="text.secondary">Saved pickup address</Typography>
                          <Typography variant="body2" sx={{ color: '#202223' }}>
                            {pickupAddressText(app.pickup_addresses.find((p) => Number(p.is_default) === 1) || app.pickup_addresses[0]) || '-'}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#6d7175' }}>No pickup address saved yet</Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email sx={{ fontSize: 16, color: '#6d7175' }} />
                    <Typography variant="body2" sx={{ color: '#202223' }}>{app.user_email || '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Phone sx={{ fontSize: 16, color: '#6d7175' }} />
                    <Typography variant="body2" sx={{ color: '#202223' }}>{app.user_contact || '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business sx={{ fontSize: 16, color: '#6d7175' }} />
                    <Typography variant="body2" sx={{ color: '#202223' }}>
                      {app.business_type ? `Premises: ${app.business_type}` : 'Premises: -'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Description sx={{ fontSize: 16, color: '#6d7175' }} />
                    <Typography variant="body2" sx={{ color: '#202223' }}>
                      {app.certificate_name || 'Certificate name: -'}
                    </Typography>
                  </Box>
                  {app.website && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Language sx={{ fontSize: 16, color: '#6d7175' }} />
                      <MuiLink href={app.website} target="_blank" rel="noopener noreferrer" variant="body2">{app.website}</MuiLink>
                    </Box>
                  )}
                  {app.registration_certificate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Description sx={{ fontSize: 16, color: '#6d7175' }} />
                      <MuiLink href={`${BASE_URL}${app.registration_certificate}`} target="_blank" rel="noopener noreferrer" variant="body2">
                        View registration certificate
                      </MuiLink>
                    </Box>
                  )}
                </Box>

                {app.approval_notes && (
                  <Alert severity={app.approval_status === 'rejected' ? 'error' : 'info'} sx={{ mt: 2 }}>
                    {app.approval_notes}
                  </Alert>
                )}
              </Card>
            ))}
          </Box>
        </Box>
      </Box>

      <Dialog open={!!viewTarget} onClose={() => setViewTarget(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {viewTarget?.business_name || 'Vendor Application'}
          <Typography variant="body2" sx={{ color: '#6d7175', mt: 0.5 }}>
            Complete seller application details and verification documents
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {viewTarget && (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip label={viewTarget.approval_status} size="small" sx={{ ...statusColor(viewTarget.approval_status), fontWeight: 600, textTransform: 'capitalize' }} />
                <Typography variant="body2" sx={{ color: '#6d7175' }}>Applied: {viewTarget.createdAt ? new Date(viewTarget.createdAt).toLocaleString() : '-'}</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box><Typography variant="caption" color="text.secondary">Applicant</Typography><Typography>{viewTarget.first_name} {viewTarget.last_name}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Business name</Typography><Typography>{viewTarget.business_name || '-'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Email</Typography><Typography sx={{ wordBreak: 'break-word' }}>{viewTarget.user_email || '-'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Phone</Typography><Typography>{viewTarget.user_contact || '-'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Business premises type</Typography><Typography sx={{ textTransform: 'capitalize' }}>{viewTarget.business_type || '-'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Certificate name / type</Typography><Typography>{viewTarget.certificate_name || '-'}</Typography></Box>
                <Box sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}>
                  <Typography variant="caption" color="text.secondary">Saved pickup addresses</Typography>
                  {viewTarget.pickup_addresses?.length ? (
                    <Box sx={{ display: 'grid', gap: 1, mt: 0.5 }}>
                      {viewTarget.pickup_addresses.map((pickup) => (
                        <Card key={pickup.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{pickup.name || 'Pickup Address'}</Typography>
                            {pickup.location_type && <Chip size="small" label={pickup.location_type} sx={{ textTransform: 'capitalize' }} />}
                            {Number(pickup.is_default) === 1 && <Chip size="small" color="success" label="Default" />}
                          </Box>
                          <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>{pickupAddressText(pickup) || '-'}</Typography>
                          {(pickup.latitude || pickup.longitude) && <Typography variant="caption" color="text.secondary">{pickup.latitude || '-'}, {pickup.longitude || '-'}</Typography>}
                        </Card>
                      ))}
                    </Box>
                  ) : (
                    <Alert severity="info" sx={{ mt: 1 }}>No pickup address has been saved by this vendor yet.</Alert>
                  )}
                </Box>
                {viewTarget.website && <Box><Typography variant="caption" color="text.secondary">Website</Typography><MuiLink href={viewTarget.website} target="_blank" rel="noopener noreferrer" sx={{ display: 'block', wordBreak: 'break-all' }}>{viewTarget.website}</MuiLink></Box>}
                {viewTarget.tax_registration_number && <Box><Typography variant="caption" color="text.secondary">Tax registration number</Typography><Typography>{viewTarget.tax_registration_number}</Typography></Box>}
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Verification certificate</Typography>
                {viewTarget.registration_certificate ? (
                  <Button component="a" href={`${BASE_URL}${viewTarget.registration_certificate}`} target="_blank" rel="noopener noreferrer" variant="outlined" startIcon={<Description />} sx={{ textTransform: 'none' }}>
                    Open uploaded certificate
                  </Button>
                ) : (
                  <Alert severity="warning">No certificate file was uploaded.</Alert>
                )}
              </Box>
              {viewTarget.approval_notes && <Alert severity={viewTarget.approval_status === 'rejected' ? 'error' : 'info'}>{viewTarget.approval_notes}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {rejectTarget?.approval_status === 'approved' ? 'Block vendor' : 'Reject application'} — {rejectTarget?.business_name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#6d7175', mb: 2 }}>
            {rejectTarget?.approval_status === 'approved'
              ? "This vendor is currently approved and can log in and sell. Blocking them will immediately stop them from logging in and managing products. A block reason is required and will be shown to the vendor in the restriction popup and notification email."
              : 'Rejection reason is mandatory and will be included in the notification email.'}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder={rejectTarget?.approval_status === 'approved' ? 'Block reason *' : 'Rejection reason *'}
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleReject} disabled={actingId === rejectTarget?.id}>
            {rejectTarget?.approval_status === 'approved' ? 'Block Vendor' : 'Reject Application'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VendorApplications;